import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { useCallback, useMemo, useRef } from 'react';
import { getSelectedShapes } from '../../model/selectors/getSelectedShapes';
import { getSnapStepPixels } from '../../model/selectors/getSnapStepPixels';
import { warehouseMapConstructorActions } from '../../model/slice/warehouseMapConstructorSlice';
import { Shape } from '../../model/types';

export interface ClipboardApi {
  copy: () => void;
  cut: () => void;
  paste: (floorId: string) => void;
  hasClipboard: () => boolean;
}

const appendCopySuffix = (label: string): string => (label.endsWith(' Copy') ? label : `${label} Copy`);

/**
 * Within-tab clipboard for map shapes. Deep-clones at copy/cut time so
 * the clipboard is immune to subsequent edits or deletions of the source
 * shapes. Paste offsets all positions by one snap step so the clone is
 * immediately visible and doesn't perfectly overlap the original, and
 * appends " Copy" to every labeled shape's name.
 */
export const useClipboard = (): ClipboardApi => {
  const dispatch = useAppDispatch();
  const selectedShapes = useAppSelector(getSelectedShapes);
  const snapStepPixels = useAppSelector(getSnapStepPixels);

  const clipboardRef = useRef<Shape[] | null>(null);

  // Stable refs so the long-lived keyboard listener always reads current
  // values without re-registering the event handler.
  const selectedShapesRef = useRef(selectedShapes);
  selectedShapesRef.current = selectedShapes;
  const snapRef = useRef(snapStepPixels);
  snapRef.current = snapStepPixels;

  const copy = useCallback(() => {
    const shapes = selectedShapesRef.current;
    if (!shapes.length) return;
    clipboardRef.current = shapes.map((s) => structuredClone(s));
  }, []);

  const cut = useCallback(() => {
    const shapes = selectedShapesRef.current;
    if (!shapes.length) return;
    clipboardRef.current = shapes.map((s) => structuredClone(s));
    dispatch(warehouseMapConstructorActions.deleteSelected());
  }, [dispatch]);

  const paste = useCallback(
    (floorId: string) => {
      const items = clipboardRef.current;
      if (!items?.length) return;

      const snap = snapRef.current;
      const clones = items.map((s) => {
        const clone = structuredClone(s);
        const base = {
          ...clone,
          id: crypto.randomUUID(),
          selected: true as const,
          floorId,
          ...('label' in clone ? { label: appendCopySuffix(clone.label) } : {}),
        };

        if ('points' in base) {
          return { ...base, points: base.points.map((p: number) => p + snap) };
        }
        return { ...base, x: base.x + snap, y: base.y + snap };
      });

      dispatch(warehouseMapConstructorActions.appendShape(clones as Shape[]));
    },
    [dispatch],
  );

  const hasClipboard = useCallback(() => Boolean(clipboardRef.current?.length), []);

  // Stable object reference — only changes when a callback identity changes,
  // which prevents the keyboard-shortcut effect from re-registering needlessly.
  return useMemo(() => ({ copy, cut, paste, hasClipboard }), [copy, cut, paste, hasClipboard]);
};
